import { captureException } from "@sentry/react";

import { MAX_ATTACHMENT_FILE_SIZE } from "./file-utils";

const CONVERT_TIMEOUT_MS = 30_000;

/**
 * Convert a QuickTime (`.mov`) upload into a progressive MP4 so browsers
 * can play it inline. Best-effort: on failure, timeout, or oversize input
 * the original file is uploaded unchanged.
 */
export async function convertMovUpload(file: File): Promise<File> {
  if (!file.name.toLowerCase().endsWith(".mov")) return file;
  if (file.size > MAX_ATTACHMENT_FILE_SIZE) return file;

  const abortController = new AbortController();
  const timeout = setTimeout(() => abortController.abort(), CONVERT_TIMEOUT_MS);
  try {
    const { convertMovToMp4 } = await import("./convert-mov-to-mp4");
    const mp4 = await convertMovToMp4(file, {
      signal: abortController.signal,
    });
    return new File([mp4], file.name.replace(/\.mov$/i, ".mp4"), {
      type: "video/mp4",
    });
  } catch (error) {
    captureException(error, {
      level: "warning",
      extra: { fileName: file.name, fileSize: file.size },
    });
    return file;
  } finally {
    clearTimeout(timeout);
  }
}
